import h from 'hyperscript';

import { scrollIntoView } from '@disy/lib/scroll-into-view';
import { uniqueId } from '@disy/lib/unique-id';
import focusOutlineStyles from '@disy/ui/focus-outline.css' with { type: 'css' };
import hiddenStyles from '@disy/ui/hidden.css' with { type: 'css' };
import { slotSummary } from '@disy/ui/section';
import sectionStyles from '@disy/ui/section.module.css' with { type: 'css' };

import './disclosure.css';
import disclosureStyles from './disclosure.module.css' with { type: 'css' };

const COMPONENT_NAME = 'd-disclosure';

export class Disclosure extends HTMLElement {
  readonly #summary: HTMLButtonElement;
  readonly #content: HTMLSlotElement;

  static get observedAttributes() {
    return ['expanded', 'non-toggleable', 'name'];
  }

  constructor() {
    super();
    const contentId = uniqueId();
    const shadowRoot = this.attachShadow({ mode: 'open' });
    shadowRoot.append(
      h('.header', { part: 'header' }, [
        h('slot.summary-aside-left', { name: 'summary-aside-left' }),
        (this.#summary = h(
          'button.summary.focus-outline-keynav',
          {
            type: 'button',
            part: 'summary',
            attrs: { 'aria-controls': contentId, 'aria-expanded': 'false' },
            onclick: () => this.#toggleExpanded(),
          },
          [h('slot', { name: 'summary' })],
        )),
        h('slot.summary-aside', { name: 'summary-aside' }),
        h('button.expanded-toggle', {
          type: 'button',
          part: 'expanded-toggle',
          attrs: { 'aria-hidden': 'true' },
          tabIndex: -1, // Remove from tab order to save a redundant tab stop.
          onclick: () => this.#toggleExpanded(),
        }),
      ]),
      (this.#content = h('slot.content', {
        id: contentId,
        part: 'content',
        attrs: { hidden: 'until-found' },
        onbeforematch: () => this.#toggleExpanded(),
      })),
    );
    shadowRoot.adoptedStyleSheets = [hiddenStyles, focusOutlineStyles, sectionStyles, disclosureStyles];
  }

  #toggleExpanded() {
    this.expanded = !this.expanded;
    scrollIntoView(this);
    this.dispatchEvent(new CustomEvent('toggle'));
  }

  #closeHomonymDisclosures() {
    if (this.expanded && this.name) {
      // Note that this only affects disclosures that are currently in the light dom
      document.querySelectorAll<Disclosure>(`.${COMPONENT_NAME}[name="${this.name}"]`).forEach((disclosure) => {
        if (disclosure !== this) {
          disclosure.expanded = false;
        }
      });
    }
  }

  connectedCallback() {
    this.classList.add(COMPONENT_NAME);
    slotSummary(this);
  }

  attributeChangedCallback() {
    const { expanded, nonToggleable } = this;
    this.#summary.setAttribute('aria-expanded', String(expanded));
    this.#summary.disabled = nonToggleable;

    this.#content.hidden = !expanded;
    if (!(expanded || nonToggleable)) {
      // Use "until-found" only when the disclosure it toggleable.
      this.#content.setAttribute('hidden', 'until-found');
    }

    this.#closeHomonymDisclosures();
  }

  get name() {
    return this.getAttribute('name') ?? '';
  }

  set name(value: string) {
    this.setAttribute('name', value);
  }

  get nonToggleable() {
    return this.hasAttribute('non-toggleable');
  }

  set nonToggleable(value: boolean) {
    this.toggleAttribute('non-toggleable', value);
  }

  get expanded() {
    return this.hasAttribute('expanded');
  }

  set expanded(value: boolean) {
    this.toggleAttribute('expanded', value);
  }
}

customElements.define(COMPONENT_NAME, Disclosure);
declare global {
  interface HTMLElementTagNameMap {
    [COMPONENT_NAME]: Disclosure;
  }
}
